export type ResponsiveViewport = "tablet" | "mobile";

export type ResponsiveViewportOverride<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  override?: boolean;
  data?: Partial<T>;
};

export type ResponsivePropsConfig<T extends Record<string, unknown> = Record<string, unknown>> = {
  mobile?: ResponsiveViewportOverride<T>;
  tablet?: ResponsiveViewportOverride<T>;
};

export type LayoutViewMode = "desktop" | "tablet" | "mobile";

export const RESPONSIVE_PROPS_KEY = "responsive";

export const resolveResponsiveFieldValue = <T extends Record<string, unknown>>(
  desktop: T,
  responsive: ResponsivePropsConfig<T> | undefined,
  viewport: LayoutViewMode,
  fieldId: keyof T & string,
): T[keyof T] => {
  if (viewport === "desktop") {
    return desktop[fieldId];
  }

  const mobileOverride = responsive?.mobile;
  const mobileValue =
    mobileOverride?.override && mobileOverride.data && fieldId in mobileOverride.data
      ? mobileOverride.data[fieldId]
      : desktop[fieldId];

  if (viewport === "mobile") {
    return mobileValue as T[keyof T];
  }

  const tabletOverride = responsive?.tablet;
  if (tabletOverride?.override && tabletOverride.data && fieldId in tabletOverride.data) {
    return tabletOverride.data[fieldId] as T[keyof T];
  }

  return mobileValue as T[keyof T];
};

export const resolveResponsiveProps = <T extends Record<string, unknown>>(
  desktop: T,
  responsive: ResponsivePropsConfig<T> | undefined,
  viewport: LayoutViewMode,
  overridableFieldIds: (keyof T & string)[],
): T => {
  const resolved = { ...desktop };
  overridableFieldIds.forEach((fieldId) => {
    resolved[fieldId] = resolveResponsiveFieldValue(
      desktop,
      responsive,
      viewport,
      fieldId,
    ) as T[keyof T & string];
  });
  return resolved;
};

export const splitDesktopAndResponsive = (props: Record<string, unknown> = {}) => {
  const { [RESPONSIVE_PROPS_KEY]: responsive, ...desktop } = props;
  return {
    desktop: desktop as Record<string, unknown>,
    responsive: responsive as ResponsivePropsConfig | undefined,
  };
};
